import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Pagination, Row, Spinner, Table } from "react-bootstrap";
import { BarChart, Bar, CartesianGrid, Legend, LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "react-toastify";
import { getUsageLimits, getUsageSummary, updateUsageLimit } from "../Components/chat/chatApi";

function DashboardPaginationBar({
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
}) {
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
      <small className="text-muted">
        Showing {start}–{end} of {total}
      </small>
      <div className="d-flex flex-wrap align-items-center gap-2">
        <span className="small text-muted">Per page</span>
        <Form.Select
          size="sm"
          style={{ width: 88 }}
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          aria-label="Rows per page"
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </Form.Select>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Pagination className="mb-0">
            <Pagination.Prev disabled={page <= 1} onClick={() => onPageChange(page - 1)} />
            <Pagination.Item active>{page}</Pagination.Item>
            <Pagination.Next disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} />
          </Pagination>
          <span className="small text-muted">of {totalPages}</span>
        </div>
      </div>
    </div>
  );
}

export default function AIUsageDashboard() {
  const [summary, setSummary] = useState(null);
  const [limits, setLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingUserId, setSavingUserId] = useState(null);

  const [topUsersPage, setTopUsersPage] = useState(1);
  const [topUsersPageSize, setTopUsersPageSize] = useState(10);
  const [limitsPage, setLimitsPage] = useState(1);
  const [limitsPageSize, setLimitsPageSize] = useState(20);
  const [limitsTotalCount, setLimitsTotalCount] = useState(0);
  const [limitsTotalPages, setLimitsTotalPages] = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryData, limitsData] = await Promise.all([
        getUsageSummary(30, topUsersPage, topUsersPageSize),
        getUsageLimits(limitsPage, limitsPageSize),
      ]);
      setSummary(summaryData);
      setLimits((limitsData.items || []).map((item) => ({ ...item })));
      const limTotal = typeof limitsData.total === "number" ? limitsData.total : (limitsData.items || []).length;
      setLimitsTotalCount(limTotal);
      if (typeof limitsData.total_pages === "number") {
        setLimitsTotalPages(limitsData.total_pages);
      } else {
        const ps = limitsData.page_size || limitsPageSize;
        setLimitsTotalPages(Math.max(1, Math.ceil(limTotal / ps) || 1));
      }
    } catch (error) {
      toast.error(error.message || "Failed to load usage dashboard.");
    } finally {
      setLoading(false);
    }
  }, [topUsersPage, topUsersPageSize, limitsPage, limitsPageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const topUsers = useMemo(() => summary?.top_users || [], [summary]);
  const topUsersPagination = summary?.top_users_pagination;

  useEffect(() => {
    if (!topUsersPagination) return;
    const maxP = topUsersPagination.total_pages;
    if (topUsersPage > maxP) setTopUsersPage(Math.max(1, maxP));
  }, [topUsersPagination, topUsersPage]);

  useEffect(() => {
    if (limitsPage > limitsTotalPages) setLimitsPage(Math.max(1, limitsTotalPages));
  }, [limitsPage, limitsTotalPages]);
  const trends = useMemo(() => summary?.trends || [], [summary]);
  const departmentUsage = useMemo(() => summary?.department_usage || [], [summary]);

  const handleLimitChange = (userId, field, value) => {
    setLimits((prev) =>
      prev.map((item) =>
        item.user_id === userId ? { ...item, [field]: value } : item
      )
    );
  };

  const handleSave = async (row) => {
    try {
      setSavingUserId(row.user_id);
      await updateUsageLimit(row.user_id, {
        max_tokens: Number(row.max_tokens || 0),
        max_cost: Number(row.max_cost || 0),
        reset_period: row.reset_period || "monthly",
      });
      toast.success("Usage limit updated.");
      await loadData();
    } catch (error) {
      toast.error(error.message || "Failed to update usage limit.");
    } finally {
      setSavingUserId(null);
    }
  };

  return (
    <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-1">AI Usage Dashboard</h4>
              <div className="text-muted">Token usage, costs, top users, and per-user limits.</div>
            </div>
            <Button variant="outline-primary" onClick={loadData} disabled={loading}>
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <Row className="g-3 mb-3">
                <Col md={4}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="text-muted small">Input tokens</div>
                      <h3>{summary?.totals?.tokens_input || 0}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="text-muted small">Output tokens</div>
                      <h3>{summary?.totals?.tokens_output || 0}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="h-100">
                    <Card.Body>
                      <div className="text-muted small">Total cost</div>
                      <h3>${Number(summary?.totals?.cost || 0).toFixed(2)}</h3>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col lg={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <Card.Title>Usage trends</Card.Title>
                      <div style={{ width: "100%", height: 280 }}>
                        <ResponsiveContainer>
                          <LineChart data={trends}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="tokens" stroke="var(--accent)" strokeWidth={2} />
                            <Line type="monotone" dataKey="cost" stroke="#10b981" strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <Card.Title>Usage by department (role)</Card.Title>
                      <div style={{ width: "100%", height: 280 }}>
                        <ResponsiveContainer>
                          <BarChart data={departmentUsage}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="department" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="tokens" fill="var(--accent)" />
                            <Bar dataKey="cost" fill="#10b981" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col>
                  <Card>
                    <Card.Body>
                      <Card.Title>Top users</Card.Title>
                      <Table responsive hover size="sm">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Tokens</th>
                            <th>Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topUsers.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-muted text-center py-3">
                                No usage in this period.
                              </td>
                            </tr>
                          ) : (
                            topUsers.map((row) => (
                              <tr key={row.user_id}>
                                <td>{row.username}</td>
                                <td>{row.tokens}</td>
                                <td>${Number(row.cost || 0).toFixed(2)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                      {topUsersPagination && (
                        <DashboardPaginationBar
                          page={topUsersPagination.page}
                          totalPages={topUsersPagination.total_pages}
                          total={topUsersPagination.total}
                          pageSize={topUsersPagination.page_size}
                          pageSizeOptions={[10, 20, 50]}
                          onPageChange={setTopUsersPage}
                          onPageSizeChange={(n) => {
                            setTopUsersPageSize(n);
                            setTopUsersPage(1);
                          }}
                        />
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card>
                <Card.Body>
                  <Card.Title>Assign usage limits</Card.Title>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Used tokens</th>
                        <th>Token %</th>
                        <th>Max tokens</th>
                        <th>Used cost</th>
                        <th>Cost %</th>
                        <th>Max cost</th>
                        <th>Reset period</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {limits.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-muted text-center py-3">
                            No users to show on this page.
                          </td>
                        </tr>
                      ) : (
                        limits.map((row) => (
                          <tr key={row.user_id}>
                            <td>{row.username}</td>
                            <td>{row.used_tokens}</td>
                            <td>{row.token_percent}%</td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                value={row.max_tokens}
                                onChange={(e) => handleLimitChange(row.user_id, "max_tokens", e.target.value)}
                              />
                            </td>
                            <td>${Number(row.used_cost || 0).toFixed(2)}</td>
                            <td>{row.cost_percent}%</td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                step="0.01"
                                value={row.max_cost}
                                onChange={(e) => handleLimitChange(row.user_id, "max_cost", e.target.value)}
                              />
                            </td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={row.reset_period}
                                onChange={(e) => handleLimitChange(row.user_id, "reset_period", e.target.value)}
                              >
                                <option value="monthly">Monthly</option>
                                <option value="weekly">Weekly</option>
                                <option value="daily">Daily</option>
                              </Form.Select>
                            </td>
                            <td>
                              <Button
                                size="sm"
                                onClick={() => handleSave(row)}
                                disabled={savingUserId === row.user_id}
                              >
                                {savingUserId === row.user_id ? "Saving..." : "Save"}
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                  <DashboardPaginationBar
                    page={limitsPage}
                    totalPages={limitsTotalPages}
                    total={limitsTotalCount}
                    pageSize={limitsPageSize}
                    pageSizeOptions={[10, 20, 50, 100]}
                    onPageChange={setLimitsPage}
                    onPageSizeChange={(n) => {
                      setLimitsPageSize(n);
                      setLimitsPage(1);
                    }}
                  />
                </Card.Body>
              </Card>
            </>
          )}
    </div>
  );
}
